'use client'

import { LineEye } from '@qr-front/icons/line-eye'
import { LineEyeOff } from '@qr-front/icons/line-eye-off'
import { LineLock } from '@qr-front/icons/line-lock'
import { Button } from '@qr-front/ui/components/Button'
import { InputField } from '@qr-front/ui/components/InputField'
import axios, { isAxiosError } from 'axios'
import { useTranslations } from 'next-intl'
import { FormEvent, useState } from 'react'

import { transformBackendResponseKey } from '@/config'
import { env } from '@/utils'

import { completeQrUnlock } from './completeQrUnlock'
import { getSafeRedirectUrl } from './getSafeRedirectUrl'

type TUnlockResponse = {
	unlockToken: string
	expiresInSec: number
	redirectUrl?: string
}

export const QrUnlockForm = ({ uri }: { uri: string }) => {
	const t = useTranslations()
	const [password, setPassword] = useState('')
	const [isVisible, setIsVisible] = useState(false)
	const [errorMessage, setErrorMessage] = useState<string>()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const RightIcon = isVisible ? LineEye : LineEyeOff

	const requestUnlock = async (): Promise<TUnlockResponse | null> => {
		try {
			const { data } = await axios.post<TUnlockResponse>(
				`${env.API_URL}/common/qr-codes/uri/${uri}/unlock`,
				{ password },
			)
			return data
		} catch (error) {
			const message = isAxiosError<{ message?: unknown }>(error)
				? error.response?.data?.message
				: undefined
			const key = transformBackendResponseKey(
				typeof message === 'string' ? message : '',
			)
			setErrorMessage(
				key && t.has(key)
					? t(key)
					: t('public__generic__unexpected_error'),
			)
			return null
		}
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		if (!password || isSubmitting) {
			return
		}
		setIsSubmitting(true)
		setErrorMessage(undefined)

		const result = await requestUnlock()
		if (!result) {
			setIsSubmitting(false)
			return
		}
		const safeRedirectUrl = getSafeRedirectUrl(
			result.redirectUrl,
			window.location.href,
		)
		if (safeRedirectUrl) {
			window.location.assign(safeRedirectUrl)
			return
		}
		try {
			await completeQrUnlock(uri, result.unlockToken, result.expiresInSec)
		} catch (error) {
			if (
				!(error as { digest?: string })?.digest?.startsWith(
					'NEXT_REDIRECT',
				)
			) {
				setErrorMessage(t('public__generic__unexpected_error'))
				setIsSubmitting(false)
			}
		}
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="flex w-full flex-col gap-4"
			noValidate
		>
			<InputField
				name="qrUnlockPassword"
				type={isVisible ? 'text' : 'password'}
				autoComplete="off"
				spellCheck={false}
				placeholder={t('public__qr_unlock__placeholder')}
				aria-label={t('public__qr_unlock__placeholder')}
				value={password}
				error={errorMessage}
				leftIcon={LineLock}
				rightIcon={RightIcon}
				rightOnClick={() => setIsVisible((current) => !current)}
				onChange={(event) => setPassword(event.target.value)}
			/>
			<Button
				type="submit"
				size="large"
				disabled={!password || isSubmitting}
				className="w-full disabled:opacity-50"
			>
				{t('public__qr_unlock__button')}
			</Button>
		</form>
	)
}
